/**
 * viewerTimeZone.ts
 * -----------------------------------------------------------------------------
 * The ONE place the browser's IANA time zone is read, and the one place the
 * `x-center-timezone` header is built.
 *
 * Rolling-date filter variables (`@today-7d`, `@startOfQuarter`, …) are resolved
 * against a calendar DAY, and which day "today" is depends on the viewer's zone.
 * The server resolves them (`resolveFilterVariables.ts`) from
 * `resolveEffectiveTimeZone(request)`, which reads this header and falls back to
 * UTC when it is absent or unrecognised. Without the header every viewer's
 * "today" is UTC's today, which is the wrong day for most of the planet for part
 * of every day.
 *
 * The same zone also has to be used by the CLIENT-side matcher
 * (`useColumnFilters`, design-system), or a column filtered in the browser and
 * the same filter executed on the server disagree about what "this quarter"
 * means. That is why the resolution lives here, in the lowest package both
 * consumers already depend on, rather than being re-read at each call site.
 * It cannot live in `@center-platform/spheres` alongside the rolling-date
 * grammar: that package already depends on this one, so the import would be a
 * build cycle (see `DEFAULT_FILTER_TIME_ZONE` below). It is also the wrong home
 * — spheres is bundled for the services too, where there is no viewer.
 */

/**
 * The zone every resolution falls back to when nothing better is known — the
 * same fallback `resolveEffectiveTimeZone` applies server-side.
 *
 * Declared here rather than imported from `@center-platform/spheres/filters`
 * (which owns `DEFAULT_FILTER_TIME_ZONE` and `isValidTimeZone`) because that
 * package already depends on THIS one: `spheres:build --> shared-api-clients:build`.
 * Importing it back would be a build cycle, so the two lines of Intl validation
 * below are owned here, at the leaf, and pinned by `viewerTimeZone.spec.ts`.
 */
const DEFAULT_FILTER_TIME_ZONE = 'UTC';

/**
 * True when `timeZone` is an IANA identifier this runtime can resolve.
 * `Intl.DateTimeFormat` throws `RangeError` for an unknown zone, which is the
 * only reliable cross-platform validation — there is no list to check against.
 * Same contract as `isValidTimeZone` in `@center-platform/spheres/filters`.
 */
function isValidTimeZone(timeZone: unknown): timeZone is string {
  if (typeof timeZone !== 'string' || timeZone.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The transport for the viewer's zone.
 *
 * Kept in lockstep with graph-service's `TIME_ZONE_HEADER`
 * (`helpers/contexts/assembleVariableContext.ts`), which is the authority on the
 * name; `viewerTimeZone.spec.ts` pins the literal so a rename on either side
 * cannot silently stop the header being read.
 */
export const TIME_ZONE_HEADER = 'x-center-timezone';

/**
 * The viewer's IANA time zone, or `undefined` when this runtime cannot name one
 * we are willing to send.
 *
 * `undefined` is a REFUSAL, not a default. Three things can go wrong, and all
 * three must end here rather than in a header:
 *
 *  - `Intl` (or `DateTimeFormat`) is absent — an old or stripped-down runtime.
 *  - `resolvedOptions().timeZone` is `undefined` or `''` — permitted by ECMA-402
 *    before the 2018 edition made the field mandatory, and still observed on
 *    some embedded engines.
 *  - The value is not a zone this runtime can resolve back (a host that reports
 *    a non-IANA alias, or a patched `Intl`).
 *
 * Sending a malformed zone would make the server log a rejection and fall back
 * to UTC anyway — the same outcome, plus noise. Omitting the header asks for the
 * documented fallback explicitly.
 */
export function resolveViewerTimeZone(): string | undefined {
  let zone: string | undefined;
  try {
    zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
  return isValidTimeZone(zone) ? zone : undefined;
}

/**
 * The zone to resolve a rolling date against in the browser, with the same UTC
 * fallback the server applies when the header is missing. Use this wherever a
 * client-side evaluator has to agree with the server; use
 * {@link resolveViewerTimeZone} where "no zone" has to stay distinguishable
 * from "UTC".
 */
export function viewerTimeZoneOrDefault(): string {
  return resolveViewerTimeZone() ?? DEFAULT_FILTER_TIME_ZONE;
}

/**
 * The zone header for one request — an empty object when no usable zone exists,
 * so nothing malformed is ever put on the wire.
 */
export function viewerTimeZoneHeaders(): Record<string, string> {
  const zone = resolveViewerTimeZone();
  return zone ? { [TIME_ZONE_HEADER]: zone } : {};
}
